import { useCallback, useEffect, useMemo } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import { useIsEmbedded } from 'hooks/useIsEmbedded';
import { List } from 'immutable';
import PropTypes from 'prop-types';

import { getActiveTipPolicyId } from 'selectors/session';

import {
  EMBEDDED_HEIGHT_ADJUSTMENT,
  HEADER_SECTION_HEIGHT,
} from 'features/timesheets/constants';
import { hasActivePolicyWithCashTips } from 'features/timesheets/redux/selectors/timesheets';
import { getShiftMetadata } from 'features/timesheets/TimesheetsPage/TimecardModal/utils.js';
import {
  EVENT_ACTIONS,
  TRACKABLE_PATHNAMES,
  trackEditModeShown,
} from 'features/timesheets/tracking';

import { cxHelpers } from 'util/className';
import { df, moment } from 'util/dateTime';

import EditHistory from '../EditHistory';
import NoteSection from '../NoteSection';
import PhotosSection from '../PhotosSection';
import { getIsLoading } from '../selectors';

import Footer from './Footer';
import Header from './Header';
import TimebreakSection from './TimebreakSection';
import TimecardSection from './TimecardSection';
import TipsSection from './TipsSection';
import { tipOutEligible } from './util';

const { cx } = cxHelpers('TimecardModalEditMode');

const EditMode = ({
  addLocationRole,
  addTimebreak,
  breakPenaltiesEnabled,
  closeModal,
  dateStr,
  deleteTimecard,
  editableShift,
  employeeOptions,
  hasTimecard,
  hideTipsSection,
  isMobile,
  isSubmitting,
  isLoading,
  mandatedBreaks,
  onChangeShift,
  onChangeFormError,
  onChangeTimebreak,
  onDisableEditMode,
  onSave,
  removeTimebreak,
  roleOptions,
  validationErrors,
  employeeName,
  changesMade,
  setChangesMade,
  locationHasActiveTipPolicy,
  locationHasActiveTipPolicyWithCashSource,
  canManageTimecards,
  shift,
  focusClockOut = false,
}) => {
  const isEmbedded = useIsEmbedded();
  const shiftStartAt = useMemo(
    () =>
      editableShift.get('actual_start_at')
        ? moment(editableShift.get('actual_start_at'))
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editableShift.get('actual_start_at')]
  );

  const shiftEndAt = useMemo(
    () =>
      editableShift.get('actual_end_at')
        ? moment(editableShift.get('actual_end_at'))
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editableShift.get('actual_end_at')]
  );

  const newTimecard = useMemo(() => {
    if (!editableShift.get('id')) {
      setChangesMade(true);
    }
    return !editableShift.get('id');
  }, [editableShift, setChangesMade]);

  const handleChangeTimecardDate = useCallback(
    date => {
      const multiDayShift =
        shiftStartAt && shiftEndAt && shiftStartAt.date() !== shiftEndAt.date();

      onChangeShift('date', date);

      if (shiftStartAt) {
        onChangeShift(
          'actual_start_at',
          `${date} ${shiftStartAt.format('HH:mm')}`
        );
      }

      if (shiftEndAt) {
        let endTime = `${date} ${shiftEndAt.format('HH:mm')}`;
        if (multiDayShift) {
          endTime = moment(endTime, df('full'))
            .add(1, 'day')
            .format(df('full'));
        }
        onChangeShift('actual_end_at', endTime);
      }
    },
    [shiftStartAt, shiftEndAt, onChangeShift]
  );

  useEffect(() => {
    if (!isLoading) {
      trackEditModeShown({
        eventAction: EVENT_ACTIONS.EDIT_STATE_SHOWN,
        properties: {
          starting_path: TRACKABLE_PATHNAMES[window.location.pathname],
          ...getShiftMetadata(editableShift),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return (
    <Box
      className={cx()}
      h={isMobile || isEmbedded ? '100%' : null}
      overflow="hidden"
      bradiusm
      bcolor="mono300"
      bw={1}
      flex
      column
      w={isMobile ? '100%' : 640}
      b={isEmbedded ? 0 : null}
    >
      {!isLoading && (
        <>
          <Box
            h={HEADER_SECTION_HEIGHT}
            pt={16}
            ph={24}
            bgcolor="mono0"
            bcolor="mono300"
            bbw={1}
            pb={16}
          >
            <Header
              employeeName={editableShift.get('employee_name')}
              roleName={editableShift.get('role_name')}
              date={moment(editableShift.get('date'))}
              locationName={editableShift.get('location_name')}
              newTimecard={newTimecard}
              onChangeShift={onChangeShift}
              addLocationRole={addLocationRole}
              onChangeTimecardDate={handleChangeTimecardDate}
              employeeOptions={employeeOptions}
              roleOptions={roleOptions}
              editableShift={editableShift}
              hasTimecard={hasTimecard}
              isMobile={isMobile}
              deleteTimecard={deleteTimecard}
              setChangesMade={setChangesMade}
            />
          </Box>
          <Box
            grow={1}
            overflowY="auto"
            maxh={`calc(80vh - ${
              isEmbedded ? EMBEDDED_HEIGHT_ADJUSTMENT : HEADER_SECTION_HEIGHT
            }px)`}
          >
            <TimecardSection
              startAt={shiftStartAt}
              endAt={shiftEndAt}
              dateStr={dateStr}
              onChangeShift={onChangeShift}
              isMobileScreenSize={isMobile}
              validationErrors={validationErrors.timecard_errors}
              scheduled={editableShift.get('scheduled')}
              scheduledStartAt={editableShift.get('scheduled_start_at')}
              scheduledEndAt={editableShift.get('scheduled_end_at')}
              setChangesMade={setChangesMade}
              shift={editableShift}
              employeeName={employeeName}
              focusClockOut={focusClockOut}
            />
            <TimebreakSection
              addTimebreak={addTimebreak}
              breakPenaltiesEnabled={breakPenaltiesEnabled}
              breakPenalty={editableShift.get('break_penalty')}
              dateStr={dateStr}
              isMobileScreenSize={isMobile}
              mandatedBreaks={mandatedBreaks || List()}
              onChange={onChangeShift}
              onChangeTimebreak={onChangeTimebreak}
              removeTimebreak={removeTimebreak}
              shiftEndAt={shiftEndAt}
              shiftStartAt={shiftStartAt}
              timebreaks={editableShift.get('timebreaks') || List()}
              validationErrors={validationErrors.timebreaks_errors}
              employeeName={employeeName}
              shift={editableShift}
              setChangesMade={setChangesMade}
            />
            {!hideTipsSection && (
              <TipsSection
                cashTips={editableShift.get('cash_tips')}
                creditTips={editableShift.get('credit_tips')}
                tipOut={editableShift.get('tip_out')}
                creditTipOutEligible={tipOutEligible({
                  timecardEligible: editableShift.get(
                    'credit_tip_out_eligible'
                  ),
                  newTimecard: !editableShift.get('timecard_id'),
                  locationHasActiveTipPolicy,
                })}
                cashTipOutEligible={tipOutEligible({
                  timecardEligible: editableShift.get('cash_tip_out_eligible'),
                  newTimecard: !editableShift.get('timecard_id'),
                  locationHasActiveTipPolicy:
                    locationHasActiveTipPolicyWithCashSource,
                })}
                tipOutUpdatedAt={editableShift.get('tip_out_updated_at')}
                onChange={onChangeShift}
                onChangeFormError={onChangeFormError}
                shift={editableShift}
                setChangesMade={setChangesMade}
              />
            )}
            {canManageTimecards && (
              <NoteSection
                employeeName={shift.get('short_name')}
                feedback={shift.get('feedback')}
                notes={shift.get('notes') || List()}
                shiftId={shift.get('id')}
              />
            )}
            <PhotosSection
              isMobile={isMobile}
              photos={shift.get('photos') || List()}
            />
            <EditHistory
              events={shift.get('edit_history_events') || List()}
              showBottomBorder={false}
            />
          </Box>
          <Footer
            shift={shift}
            isMobile={isMobile}
            onCancel={onDisableEditMode}
            onSave={onSave}
            isSubmitting={isSubmitting}
            editableShift={editableShift}
            closeModal={closeModal}
            changesMade={changesMade}
            setChangesMade={setChangesMade}
          />
        </>
      )}
    </Box>
  );
};

EditMode.propTypes = {
  addTimebreak: PropTypes.func.isRequired,
  breakPenaltiesEnabled: PropTypes.bool,
  dateStr: PropTypes.string,
  editableShift: ImmutablePropTypes.map.isRequired,
  isMobile: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  isLoading: PropTypes.bool,
  mandatedBreaks: ImmutablePropTypes.list.isRequired,
  onChangeShift: PropTypes.func.isRequired,
  onChangeTimebreak: PropTypes.func.isRequired,
  removeTimebreak: PropTypes.func.isRequired,
  validationErrors: PropTypes.object.isRequired,
  employeeName: PropTypes.string,
  changesMade: PropTypes.bool.isRequired,
  setChangesMade: PropTypes.func.isRequired,
  locationHasActiveTipPolicy: PropTypes.bool,
  locationHasActiveTipPolicyWithCashSource: PropTypes.bool,
  canManageTimecards: PropTypes.bool.isRequired,
  shift: ImmutablePropTypes.map.isRequired,
  focusClockOut: PropTypes.bool,
};

export default connect(state => ({
  locationHasActiveTipPolicy: getActiveTipPolicyId(state),
  locationHasActiveTipPolicyWithCashSource: hasActivePolicyWithCashTips(state),
  isLoading: getIsLoading(state),
}))(EditMode);
